import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import GeneralListItem from "@/components/general/generaListItem";
import { IAppointment } from "@/types/Entities";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { Tooltip } from "@mui/material";
import { api } from "@/helper/api";
import { useDispatch, useSelector } from "react-redux";
import { setAppointmentDashboard } from "@/components/state/appointment/appointmentDashboardSlice";
import { RootState } from "@/components/state/store";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { usePageHeading } from "@/helper/customHooks/usePageHeading";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import DropDown from "@/components/forms/inputs/dropDown";
import { setAppointmentDashboardSelectedCalendarDate } from "@/components/state/appointmentDashboard/selectedCalendarDaySlice";
import {
  resetAppointmentDashboardCombinedFilters,
  setAppointmentDashboardCombinedFilter,
} from "@/components/state/appointmentDashboard/combinedFilterSlice";
import { useActiveKaerUsers } from "@/helper/customHooks/useActiveKaerUsers";
import {
  getAppointmentDetails,
  getTranslatedOptionObject,
} from "@/helper/statusOptions";
import styles from "@/components/appointmentDashboard/appointmentList.module.css";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import AddIcon from "@mui/icons-material/Add";
import PersonIcon from "@mui/icons-material/Person";
import BusinessIcon from "@mui/icons-material/Business";
import PreviewIcon from "@mui/icons-material/Preview";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import SubjectOverviewAppointmentCreateModal from "@/components/subjectOverview/subjectOverviewAppointmentCreateModal";
import { cloneDeep, isEqual } from "lodash";
import { SubjectSearchSelect } from "@/components/subjectSearch/subjectSearchSelect";
import { useSubjectSearch } from "@/helper/customHooks/useSubjectSearch";
import { KaerIcon } from "@/icons/kaerIcon";
import "dayjs/locale/de";
import { FormFieldLabel } from "@/components/forms/inputs/formFieldLabel";

type SubjectFilteringParameters = "customer" | "company" | "all";

interface FiltersState {
  status: { value: string; label: string }[];
  mainCategory: { value: string; label: string }[];
  executedBy: { value: string; label: string }[];
  assistedBy: { value: string; label: string }[];
  subject: { value: string; label: string }[];
  company: { value: string; label: string; companyId: string } | any;
  isAnalog: { value: string; label: string } | null;
  includeCanceled: boolean;
}

function AppointmentList() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const combinedFilter = useSelector(
    (state: RootState) => state.appointmentDashboardCombinedFilter,
  );
  const persistedSelectedDate = useSelector(
    (state: RootState) => state.appointmentDashboard,
  );
  const selectedCalendarDay = useSelector(
    (state: RootState) => state.appointmentDashboardSelectedCalendarDate,
  );
  const [appointmentDays, setAppointmentDays]: any = useState<string[]>([]);
  const [daysWithAppointments, setDaysWithAppointments]: any = useState({});
  const [selectedDay, setSelectedDay] = useState<any>(
    new Date(
      selectedCalendarDay.value || persistedSelectedDate.selectedDashboardDate,
    ),
  );
  usePageHeading(
    `${t("appointment_dashboard")} (${dayjs(selectedDay).format("DD.MM.YYYY")})`,
  );
  const [appointmentsForSelectedDay, setAppointmentsForSelectedDay]: any =
    useState<IAppointment[]>([]);
  const [activeStarteDate, setActiveStartDate] = useState<Date>();
  const { handleError, errorModal } = useErrorModal();
  /* eslint-disable */
  const [subjectOptions, setSubjectOptions] = useState<any>([]);
  const [companyOptions, setCompanyOptions] = useState<any>([]);
  /* eslint-enable */
  const [statusOptions, setStatusOptions] = useState<any>([]);
  const [mainCategoryOptions, setMaincategoryOptions] = useState<any>([]);
  const { userOptions } = useActiveKaerUsers({ onError: handleError });
  const [isLoadingAppointments, setIsLoadingAppointments] = useState(true);

  const [openAppointmentCreateModal, setOpenAppointmentCreateModal] =
    useState(false);
  const [selectedSubjectFiltering, setSelectedSubjectFiltering] =
    useState<SubjectFilteringParameters>("all");
  const [appointmentCount, setAppointmentCount] = useState<number>(0);
  const [showCalendar, setShowCalendar] = useState<boolean>(true);

  const today = dayjs(new Date());

  const prevCombinedFilterRef = useRef(cloneDeep(combinedFilter));

  const prevSelectedDayRef = useRef<Date | null>(null);
  const prevSubjectFilterRef = useRef(selectedSubjectFiltering);

  const isFirstRender = useRef(true);

  const subjectFilteringOptions = [
    { value: "customer", label: t("customer") },
    { value: "company", label: t("companies") },
    { value: "all", label: t("appointmentdashboard_subject_filterlabel") },
  ];

  useEffect(() => {
    getMainCategoryOptions(setMaincategoryOptions, handleError);
    getAppointmentStatusOptions();

    reloadAppointmentDays({
      month: dayjs().month(),
      year: dayjs().year(),

      shouldNotCorrectMonth: false,
    });
  }, []);

  useEffect(() => {
    const filtersChanged = !isEqual(
      JSON.parse(JSON.stringify(prevCombinedFilterRef.current)),
      JSON.parse(JSON.stringify(combinedFilter)),
    );

    const subjectFilterChanged =
      prevSubjectFilterRef.current !== selectedSubjectFiltering;

    if (filtersChanged || subjectFilterChanged) {
      reloadAppointmentDays({
        month: dayjs(activeStarteDate).format("M"),
        year: dayjs(activeStarteDate).format("YYYY"),

        shouldNotCorrectMonth: true,
      });
    }
  }, [activeStarteDate, selectedSubjectFiltering, combinedFilter]);

  useEffect(() => {
    const daysWithAppointments: any = {};

    for (const key in appointmentDays) {
      const dateOfAppointment = new Date(appointmentDays[key]);
      const appointmentYear = dateOfAppointment.getFullYear();
      const appointmentMonth = dateOfAppointment.getMonth();
      const appointmentDay = dateOfAppointment.getDate();

      if (
        !Object.getOwnPropertyDescriptor(daysWithAppointments, appointmentYear)
      ) {
        daysWithAppointments[appointmentYear] = {};
      }

      if (
        !Object.getOwnPropertyDescriptor(
          daysWithAppointments[appointmentYear],
          appointmentMonth,
        )
      ) {
        daysWithAppointments[appointmentYear][appointmentMonth] = {};
      }

      daysWithAppointments[appointmentYear][appointmentMonth][appointmentDay] =
        true;
    }

    setDaysWithAppointments(daysWithAppointments);
  }, [appointmentDays]);

  useEffect(() => {
    const applyFilter = () => {
      const dateCopy = new Date(selectedDay);

      dateCopy.setHours(12); // quick hack, because of timesaving it will be otherwise the day before
      const dateStringForSelectedDay = dateCopy.toISOString().split("T")[0];

      setAppointmentCount(0);

      const additionalUrlParameters: Record<string, any> = {
        date: dateStringForSelectedDay,
      };
      const additionalRouteParts: Record<string, any> = { dashboard: "/" };

      const {
        status: statusFilter,
        mainCategory: mainCategoryFilter,
        executedBy: executedByFilter,
        assistedBy: assistedByFilter,
        subject: subjectFilter,
        company: companyFilter,
        isAnalog: isAnalogFilter,
        includeCanceled,
      } = combinedFilter;

      const statusMappings: Record<string, string[]> = {
        in_progress: [
          "in_progress",
          "post_processing_required",
          "scheduled",
          "approved",
        ],
        in_review: ["waiting_for_review"],
      };

      if (Array.isArray(statusFilter) && statusFilter.length > 0) {
        const mappedStatuses = statusFilter.map(
          (status: any) => statusMappings[status.value] || [status.value],
        );
        additionalUrlParameters["status__in"] = mappedStatuses;
      }
      if (Array.isArray(mainCategoryFilter) && mainCategoryFilter.length > 0) {
        additionalUrlParameters["category__in"] = mainCategoryFilter.map(
          (category: any) => category.value,
        );
      }
      if (Array.isArray(executedByFilter) && executedByFilter.length > 0) {
        additionalUrlParameters["executed_by__in"] = executedByFilter.map(
          (executedBy: any) => executedBy.value,
        );
      }
      if (Array.isArray(assistedByFilter) && assistedByFilter.length > 0) {
        additionalUrlParameters["assisted_by__in"] = assistedByFilter.map(
          (assistedBy: any) => assistedBy.value,
        );
      }

      if (Array.isArray(companyFilter) && companyFilter.length > 0) {
        additionalUrlParameters["subject__company__in"] = companyFilter.map(
          (company: any) => company.companyId,
        );
        additionalUrlParameters["subject__is_company"] = "false";
      }

      if (Array.isArray(subjectFilter) && subjectFilter.length > 0) {
        additionalUrlParameters["subject_id__in"] = subjectFilter.map(
          (subject: any) => subject.value,
        );
      }

      if (selectedSubjectFiltering === "company")
        additionalUrlParameters["subject__is_company"] = true;
      if (selectedSubjectFiltering === "customer")
        additionalUrlParameters["subject__is_company"] = false;

      if (
        isAnalogFilter &&
        typeof isAnalogFilter === "object" &&
        isAnalogFilter.value !== ""
      ) {
        additionalUrlParameters["subject_is_analog"] = isAnalogFilter.value;
      }
      if (includeCanceled) {
        additionalUrlParameters["include_canceled"] = "true";
      }

      setIsLoadingAppointments(true);
      getAppointments(
        setAppointmentsForSelectedDay,
        additionalUrlParameters,
        additionalRouteParts,
        handleError,
        setAppointmentCount,
        () => setIsLoadingAppointments(false),
      );
    };

    const filtersChanged = !isEqual(
      JSON.parse(JSON.stringify(prevCombinedFilterRef.current)),
      JSON.parse(JSON.stringify(combinedFilter)),
    );
    const dateChanged =
      !prevSelectedDayRef.current ||
      prevSelectedDayRef.current.toISOString() !== selectedDay.toISOString();

    const subjectFilterChanged =
      prevSubjectFilterRef.current !== selectedSubjectFiltering;

    if (
      isFirstRender.current ||
      filtersChanged ||
      dateChanged ||
      subjectFilterChanged
    ) {
      applyFilter();
      isFirstRender.current = false;

      prevCombinedFilterRef.current = JSON.parse(
        JSON.stringify(combinedFilter),
      );

      prevSelectedDayRef.current = selectedDay;
      prevSubjectFilterRef.current = selectedSubjectFiltering;
    }
  }, [selectedDay, combinedFilter, selectedSubjectFiltering]);

  const reloadAppointmentDays = ({
    month,
    year,
    shouldNotCorrectMonth = false,
  }: {
    month: any;
    year: any;
    onSuccess?: () => void;
    shouldNotCorrectMonth?: boolean;
  }) => {
    const correctMonth = shouldNotCorrectMonth ? month : month + 1;
    const safeMonth = Math.min(12, Math.max(1, correctMonth));

    const additionalUrlParameters: Record<string, any> = {
      month: safeMonth,
      year,
    };

    const additionalRouteParts: Record<string, any> = {
      fetch_available_appointments: "/",
    };

    const {
      status: statusFilter,
      mainCategory: mainCategoryFilter,
      executedBy: executedByFilter,
      assistedBy: assistedByFilter,
      subject: subjectFilter,
      company: companyFilter,
      isAnalog: isAnalogFilter,
      includeCanceled,
    } = combinedFilter;

    const statusMappings: Record<string, string[]> = {
      in_progress: [
        "in_progress",
        "post_processing_required",
        "scheduled",
        "approved",
      ],
      in_review: ["waiting_for_review", "review_preperation_required"],
    };

    //status
    if (Array.isArray(statusFilter) && statusFilter.length > 0) {
      const mappedStatuses = statusFilter.map(
        (status: any) => statusMappings[status.value] || [status.value],
      );
      additionalUrlParameters["status__in"] = mappedStatuses;
    } else if (typeof statusFilter === "string" && statusFilter !== "") {
      additionalUrlParameters["status__in"] = statusMappings[statusFilter] || [
        statusFilter,
      ];
    }
    //category
    if (Array.isArray(mainCategoryFilter) && mainCategoryFilter.length > 0) {
      additionalUrlParameters["category__in"] = mainCategoryFilter.map(
        (category: any) => category.value,
      );
    } else if (
      typeof mainCategoryFilter === "string" &&
      mainCategoryFilter !== ""
    ) {
      additionalUrlParameters["category__in"] = [mainCategoryFilter];
    }
    //executedBy
    if (Array.isArray(executedByFilter) && executedByFilter.length > 0) {
      additionalUrlParameters["executed_by__in"] = executedByFilter.map(
        (executedBy: any) => executedBy.value,
      );
    } else if (
      typeof executedByFilter === "string" &&
      executedByFilter !== ""
    ) {
      additionalUrlParameters["executed_by__in"] = [executedByFilter];
    }
    //assistedBy
    if (Array.isArray(assistedByFilter) && assistedByFilter.length > 0) {
      additionalUrlParameters["assisted_by__in"] = assistedByFilter.map(
        (assistedBy: any) => assistedBy.value,
      );
    } else if (
      typeof executedByFilter === "string" &&
      executedByFilter !== ""
    ) {
      additionalUrlParameters["executed_by__in"] = [executedByFilter];
    }
    //company

    if (Array.isArray(companyFilter) && companyFilter.length > 0) {
      additionalUrlParameters["subject__company__in"] = companyFilter.map(
        (company: any) => company.companyId,
      );
      additionalUrlParameters["subject__is_company"] = "false";
    }

    if (Array.isArray(subjectFilter) && subjectFilter.length > 0) {
      additionalUrlParameters["subject_id__in"] = subjectFilter.map(
        (subject) => subject.value,
      );
    }

    if (selectedSubjectFiltering === "company") {
      additionalUrlParameters["subject__is_company"] = true;
    } else if (selectedSubjectFiltering === "customer") {
      additionalUrlParameters["subject__is_company"] = false;
    }

    if (
      isAnalogFilter &&
      typeof isAnalogFilter === "object" &&
      isAnalogFilter.value !== ""
    ) {
      additionalUrlParameters["subject_is_analog"] = isAnalogFilter.value;
    }
    if (includeCanceled) {
      additionalUrlParameters["include_canceled"] = "true";
    }

    getDistinctDayAppointments(
      setAppointmentDays,
      additionalRouteParts,
      additionalUrlParameters,
      handleError,
    );
  };

  const getAppointmentStatusOptions = () => {
    const additionalRouteParts: any = {};
    additionalRouteParts["available_statuses"] = "/";
    api.genericApiRequest({
      entity: "appointment",
      method: "get",
      additionalRouteParts: additionalRouteParts,
      successHandler: (res: any) => {
        const statusWhitelist = [
          "open",
          "prefilled_by_user",
          "in_progress",
          "waiting_for_lab_results",
          "evaluating_lab_results",
          "waiting_for_review",
          "completed",
          "missed",
          "declined",
          "post_processing_required",
          "canceled_not_in_time",
          "review_preparation_required",
        ];
        const tempStatusOptionArray = res.data.results
          .map((item: any) =>
            getTranslatedOptionObject(item, false, statusWhitelist),
          )
          .filter(Boolean)
          .sort(
            (a: any, b: any) =>
              statusWhitelist.indexOf(a.value) -
              statusWhitelist.indexOf(b.value),
          );
        setStatusOptions(tempStatusOptionArray);
      },
      failHandler: (error: any) => {
        handleError(error);
      },
    });
  };

  //eslint-disable-next-line
  const handleMonthChange = ({ action, activeStartDate, value, view }: any) => {
    activeStartDate.setHours(12);
    setActiveStartDate(activeStartDate.setHours(12));

    reloadAppointmentDays({
      month: activeStartDate.getMonth(),
      year: activeStartDate.getFullYear(),

      shouldNotCorrectMonth: false,
    });
  };
  //added a second case because of the different metricNaming in LiveSystem
  const getAppointmentAddress = (appointment: any) => {
    const buildingInformation = appointment?.building_information;

    if (
      buildingInformation.name &&
      buildingInformation.street_house &&
      buildingInformation.zip &&
      buildingInformation.city
    ) {
      return (
        buildingInformation.name +
        " (" +
        buildingInformation.street_house +
        ", " +
        buildingInformation.zip +
        " " +
        buildingInformation.city +
        ")"
      );
    } else {
      return "-";
    }
  };

  const preRenderItemsForSelectedDay = () => {
    const itemsPerHour: any = {};
    const preRenderedItems = [];

    for (const key in appointmentsForSelectedDay) {
      const appointment = appointmentsForSelectedDay[key];
      const dateObject = new Date(appointment.datetime);
      const appointmentExaminations = [];
      const appointmentAddress = getAppointmentAddress(appointment);
      const isCompany = appointment.subject?.company.subject_id ? false : true;

      for (const key in appointment.examination_set) {
        appointmentExaminations.push(appointment.examination_set[key]);
      }
      let mainCategoryName = appointment?.category?.name;
      if (mainCategoryName === undefined) {
        mainCategoryName = "-";
      }
      const { backgroundColor, appointmentStatus } = getAppointmentDetails(
        appointment.status,
      );

      const canceledStatuses = ["canceled", "canceled_not_in_time"];
      const href = canceledStatuses.includes(appointment.status)
        ? `/settings/appointmentEditor/${appointment.id}`
        : `/appointmentDashboard/${appointment.id}`;
      const item = (
        <GeneralListItem
          key={appointment.id}
          onClickElement="wrapper"
          itemBackgroundColor={backgroundColor}
          hideButton={true}
          margin="1rem 0"
        >
          <Link to={href} className={styles.generalListItemLink}>
            <table className={styles.tableStyle} border={0}>
              <colgroup>
                <col className={styles.col20} />
                <col className={styles.col40} />
                <col className={styles.col20} />
                <col className={styles.col10} />
              </colgroup>
              <tbody>
                <tr>
                  <td className={styles.cellStyle}>
                    <strong>
                      {dateObject.toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}{" "}
                      {t("time_indicator")}
                    </strong>
                  </td>
                  <td className={styles.cellStyle}>
                    {t("company")}: <strong>{appointment.company_name}</strong>
                  </td>
                  <td className={styles.cellStyle}>
                    {t("responsible_person")}:{" "}
                    <strong>{appointment?.executed_by?.name}</strong>
                  </td>
                  <td>{isCompany ? <BusinessIcon /> : <PersonIcon />}</td>
                </tr>
                {appointment.assisted_by && (
                  <tr>
                    <td className={styles.cellStyle}></td>
                    <td className={styles.cellStyle}></td>
                    <td className={styles.cellStyle}>
                      {t("assistance_person")}:{" "}
                      <strong>{appointment?.assisted_by?.name}</strong>
                    </td>
                    <td></td>
                  </tr>
                )}
                <tr>
                  <td rowSpan={2} className={styles.cellStyle}>
                    {appointmentExaminations
                      ?.map((examination: any) => examination.name)
                      .join(", ")}
                  </td>
                  <td className={styles.cellStyle}>
                    {t("location")}: <strong>{appointmentAddress}</strong>
                  </td>
                  <td className={styles.cellStyle}>
                    {t("main_category")}: <strong>{mainCategoryName}</strong>
                  </td>
                </tr>
                <tr>
                  <td className={styles.cellStyle}>
                    {t("test_subject")}:{" "}
                    <strong>
                      {(() => {
                        const name = appointment.subject.name;
                        const birthDate = appointment.subject.birth_date;
                        if (birthDate) {
                          const birth = dayjs(birthDate);
                          const now = dayjs();
                          const age = now.diff(birth, "year");
                          const formattedBirth = birth.format("DD.MM.YYYY");
                          return `${name} (${formattedBirth}, ${age} ${t(
                            "years_old",
                          )})`;
                        }
                        return name;
                      })()}
                    </strong>
                  </td>
                  <td className={styles.cellStyle}>
                    {t("status")}: <strong>{appointmentStatus}</strong>
                  </td>
                </tr>
              </tbody>
            </table>
          </Link>
        </GeneralListItem>
      );

      const appointmentHour = dateObject.getHours();

      if (!Object.getOwnPropertyDescriptor(itemsPerHour, appointmentHour)) {
        itemsPerHour[appointmentHour] = [];
      }

      itemsPerHour[appointmentHour].push(item);
    }

    for (const i in Object.keys(itemsPerHour)) {
      const key = Object.keys(itemsPerHour)[i];
      preRenderedItems.push(
        <h2 key={key + "_timeslot_for_day"}>
          {key}:00 {t("time_indicator")}
        </h2>,
      );
      for (const j in itemsPerHour[key]) {
        const appointment = itemsPerHour[key][j];
        preRenderedItems.push(appointment);
      }
    }

    return preRenderedItems;
  };

  const calendarTileDisabled = ({ date, view }: any) => {
    if (view === "month") {
      if (
        daysWithAppointments[date.getFullYear()] &&
        daysWithAppointments[date.getFullYear()][date.getMonth()] &&
        daysWithAppointments[date.getFullYear()][date.getMonth()][
          date.getDate()
        ]
      ) {
        return false;
      }
    }

    return true;
  };

  const onChange = (selectedDay: any, focus?: boolean) => {
    setSelectedDay(selectedDay);
    dispatch(
      setAppointmentDashboard({
        selectedDashboardDate: selectedDay.toISOString(),
      }),
    );
    if (focus) {
      setActiveStartDate(undefined);
    }
    setAppointmentsForSelectedDay([]);
    dispatch(
      setAppointmentDashboardSelectedCalendarDate(selectedDay.toISOString()),
    );
  };

  //handleDropDownFilterLogic - set to CombinedState and dispatch to CombinedGlobalState
  const handleFilter = <K extends keyof FiltersState>(
    name: K,
    value: FiltersState[K],
  ) => {
    if (name === "status") {
      dispatch(setAppointmentDashboardCombinedFilter({ name, value }));
    } else if (name === "mainCategory") {
      dispatch(setAppointmentDashboardCombinedFilter({ name, value }));
    } else if (name === "executedBy") {
      dispatch(setAppointmentDashboardCombinedFilter({ name, value }));
    } else if (name === "assistedBy") {
      dispatch(setAppointmentDashboardCombinedFilter({ name, value }));
    } else if (name === "company") {
      dispatch(setAppointmentDashboardCombinedFilter({ name, value }));
    } else if (name === "subject") {
      dispatch(setAppointmentDashboardCombinedFilter({ name, value }));
    } else {
      if (value !== undefined) {
        dispatch(setAppointmentDashboardCombinedFilter({ name, value }));
      }
    }
  };
  //handleDropDownFilterLogic - dispatch/clear entry of CombinedGlobal & localState
  const handleClearDropdown = (name: any) => {
    const getEmptyValue = (fieldName: keyof FiltersState) => {
      switch (fieldName) {
        case "status":
          return [];
        case "assistedBy":
          return [];
        case "executedBy":
          return [];
        case "mainCategory":
          return [];
        case "subject":
          return { value: "", label: "" };
        case "company":
          return { value: "", label: "", companyId: "" };
        case "isAnalog":
          return null;
        case "includeCanceled":
          return false;
        default:
          return "";
      }
    };
    const emptyValue = getEmptyValue(name);

    dispatch(
      setAppointmentDashboardCombinedFilter({
        name,
        value: emptyValue,
      }),
    );
  };

  const handleSetQueryParameter = (selectedOption: any) => {
    if (!selectedOption || !selectedOption.value) {
      return;
    }

    if (selectedOption.value === selectedSubjectFiltering) {
      return;
    }

    setSelectedSubjectFiltering(selectedOption.value);
  };

  const { fetchSubjects } = useSubjectSearch({
    subdomain: "admin",
    allowSubjectSearchForNonAdmin: false,
  });

  return (
    <div className={styles.listWrapper}>
      <section style={{ height: "100%" }}>
        <div className={styles.topFilterWrapper}>
          <div className={styles.subjectFilterDropdownWrapper}>
            <DropDown
              key={selectedSubjectFiltering}
              options={subjectFilteringOptions}
              onChange={handleSetQueryParameter}
              defaultValue={selectedSubjectFiltering}
            />
          </div>
          <Tooltip
            placement="bottom"
            arrow
            title={
              combinedFilter.includeCanceled
                ? t("exclude_canceled_appointments")
                : t("include_canceled_appointments")
            }
          >
            <GenericButton
              onClick={() => {
                dispatch(
                  setAppointmentDashboardCombinedFilter({
                    name: "includeCanceled",
                    value: !combinedFilter.includeCanceled,
                  }),
                );
              }}
            >
              {combinedFilter.includeCanceled ? (
                <EventBusyIcon />
              ) : (
                <PreviewIcon />
              )}
            </GenericButton>
          </Tooltip>

          <GenericButton
            variant="contained"
            color="primary"
            onClick={() => setOpenAppointmentCreateModal(true)}
          >
            <span className={styles.addColumnButtonContent}>
              <AddIcon />
            </span>
          </GenericButton>
          <Tooltip
            title={t("number_of_appointments_for_current_day_filter")}
            placement="bottom"
            arrow
          >
            <div className={styles.appointmentCountText}>
              <span>
                {appointmentCount} {t("appointment_multi")}
              </span>
            </div>
          </Tooltip>
        </div>
      </section>

      <div className={styles.containerGrid}>
        {/* left container */}
        <div className={styles.containerLeft}>
          <div className={styles.appointmentContentListWrapper}>
            {(() => {
              const items = preRenderItemsForSelectedDay();
              if (items.length > 0) {
                return items;
              }
              return (
                <p className={styles.emptyState}>
                  {isLoadingAppointments
                    ? t("loading")
                    : t("appointmentdashboard_no_appointments_for_day")}
                </p>
              );
            })()}
          </div>
        </div>

        {/* right container */}
        <div className={styles.containerRight}>
          <section className={styles.appointmentContentFilterWrapper}>
                <section className={styles.showCalendarWrapper}>
                  <GenericButton
                    variant="icon"
                    color="primary"
                    onClick={() => setShowCalendar((v) => !v)}
                  >
                    <KaerIcon
                      icon="calendar"
                      color="#8c1ec8"
                      height="24"
                      width="24"
                    />
                  </GenericButton>
                  {!showCalendar && (
                    <span className={styles.hideCalendarDateSpan}>
                      {dayjs(selectedDay).locale("de").format("DD MMMM YYYY")}
                    </span>
                  )}
                </section>

                {showCalendar && (
                  <Calendar
                    onChange={(selectedDay) => onChange(selectedDay)}
                    minDetail={"month"}
                    onActiveStartDateChange={handleMonthChange}
                    tileDisabled={calendarTileDisabled}
                    value={selectedDay}
                  />
                )}

                <section className={styles.calendarButtonContainer}>
                  <GenericButton
                    onClick={() => onChange(new Date(), true)}
                    variant="outlined"
                    disabled={
                      today.format("DD/MM/YYYY") ===
                        dayjs(selectedDay).format("DD/MM/YYYY") &&
                      today.format("DD/MM/YYYY") ===
                        dayjs(selectedDay).format("DD/MM/YYYY") &&
                      dayjs(activeStarteDate).format("MM") ===
                        today.format("MM")
                    }
                  >
                    {t("date_today")}
                  </GenericButton>
                  <GenericButton
                    variant="outlined"
                    onClick={() => onChange(new Date(selectedDay), true)}
                    disabled={
                      dayjs(activeStarteDate).format("MM") ===
                      dayjs(selectedDay).format("MM")
                    }
                  >
                    {t("date_selected")}
                  </GenericButton>
                </section>
                <GenericButton
                  variant="outlined"
                  onClick={() => {
                    dispatch(resetAppointmentDashboardCombinedFilters());
                    setSelectedSubjectFiltering("all");
                  }}
                >
                  {t("reset_filters")}
                </GenericButton>
                <DropDown
                  options={mainCategoryOptions}
                  onChange={(event: any) => handleFilter("mainCategory", event)}
                  name={t("main_category")}
                  label={t("main_category")}
                  isClearable={true}
                  resetInput={() => handleClearDropdown("mainCategory")}
                  defaultValue={combinedFilter.mainCategory}
                  isMulti={true}
                  useReduxDefaultValue={true}
                />

                <DropDown
                  options={statusOptions}
                  onChange={(event: any) => handleFilter("status", event)}
                  name={t("status")}
                  label={t("status")}
                  isClearable={true}
                  resetInput={() => handleClearDropdown("status")}
                  defaultValue={combinedFilter.status}
                  isMulti={true}
                  useReduxDefaultValue={true}
                />

                <DropDown
                  options={userOptions}
                  onChange={(event: any) => handleFilter("executedBy", event)}
                  name={t("executed_by")}
                  label={t("executed_by")}
                  isClearable={true}
                  resetInput={() => handleClearDropdown("executedBy")}
                  defaultValue={combinedFilter.executedBy}
                  isMulti={true}
                  useReduxDefaultValue={true}
                />
                <DropDown
                  options={userOptions}
                  onChange={(event: any) => handleFilter("assistedBy", event)}
                  name={t("assisted_by")}
                  label={t("assisted_by")}
                  isClearable={true}
                  resetInput={() => handleClearDropdown("assistedBy")}
                  defaultValue={combinedFilter.assistedBy}
                  isMulti={true}
                  useReduxDefaultValue={true}
                />
                <div className={styles.subjectSearchField}>
                  <FormFieldLabel
                    label={t("related_parent_company")}
                    htmlFor="appointment-dashboard-company-filter"
                  />
                  <SubjectSearchSelect
                    name="appointment-dashboard-company-filter"
                    onChange={(event: any) => handleFilter("company", event)}
                    isClearable={true}
                    isMulti={true}
                    loadOptions={(input: any) =>
                      fetchSubjects(input, { queryParameter: "companies" })
                    }
                    value={combinedFilter.company}
                  />
                </div>
                <div className={styles.subjectSearchField}>
                  <FormFieldLabel
                    label={t("appointmentdashboard_subject_filterlabel")}
                    htmlFor="appointment-dashboard-subject-filter"
                  />
                  <SubjectSearchSelect
                    name="appointment-dashboard-subject-filter"
                    onChange={(event: any) => handleFilter("subject", event)}
                    isClearable={true}
                    isMulti={true}
                    loadOptions={(input: any) => fetchSubjects(input)}
                    value={combinedFilter.subject}
                  />
                </div>
                <DropDown
                  options={[
                    { value: "true", label: t("yes") },
                    { value: "false", label: t("no") },
                  ]}
                  onChange={(event: any) => handleFilter("isAnalog", event)}
                  name={t("analog_user")}
                  label={t("analog_user")}
                  isClearable={true}
                  resetInput={() => handleClearDropdown("isAnalog")}
                  defaultValue={combinedFilter.isAnalog}
                  useReduxDefaultValue={true}
                  wrapperClassName={styles.extraMb}
                />
          </section>
        </div>
        {errorModal}
        {openAppointmentCreateModal && (
          <SubjectOverviewAppointmentCreateModal
            open={openAppointmentCreateModal}
            onClose={() => setOpenAppointmentCreateModal(false)}
            onAppointmentCreated={() => {
              // Reload appointments for the current selected day
              const additionalUrlParameters: any = {};
              const additionalRouteParts: any = {};
              additionalRouteParts["dashboard"] = "/";
              additionalUrlParameters["date"] = selectedDay
                .toISOString()
                .split("T")[0];

              setIsLoadingAppointments(true);
              getAppointments(
                setAppointmentsForSelectedDay,
                additionalUrlParameters,
                additionalRouteParts,
                handleError,
                setAppointmentCount,
                () => setIsLoadingAppointments(false),
              );

              // Also reload the calendar days to update the dots
              reloadAppointmentDays({
                month: selectedDay.getMonth(),
                year: selectedDay.getFullYear(),
                shouldNotCorrectMonth: true,
              });
            }}
          />
        )}
      </div>
    </div>
  );
}
export default AppointmentList;

function getMainCategoryOptions(setMaincategoryOptions: any, handleError: any) {
  api.genericApiRequest({
    method: "get",
    entity: "mainCategory",
    successHandler: (res: any) => {
      const data = res.data.results;
      setMaincategoryOptions(
        data.map((cat: any) => ({ value: cat.id, label: cat.name })),
      );
    },
    failHandler: (error: any) => handleError(error),
  });
}

function getAppointments(
  setAppointmentsForSelectedDay: any,
  additionalUrlParameters: any,
  additionalRouteParts: any,
  handleError: any,
  setAppointmentCount?: any,
  onSettled?: () => void,
) {
  api.genericApiRequest({
    entity: "appointment",
    method: "get",
    successHandler: (res: any) => {
      const data = res.data.results;
      setAppointmentsForSelectedDay(data);
      if (setAppointmentCount && res.data.count !== undefined) {
        setAppointmentCount(res.data.count);
      }
      onSettled?.();
    },
    failHandler: (error: any) => {
      handleError(error);
      onSettled?.();
    },
    additionalRouteParts: additionalRouteParts,
    parametersToRender: {
      limit: "150",
      offset: "0",
      depth: "1",
      additionalUrlParameters: additionalUrlParameters,
    },
  });
}

function getDistinctDayAppointments(
  setAppointmentDays: any,
  additionalRouteParts: any,
  additionalUrlParameters: any,
  handleError: any,
) {
  api.genericApiRequest({
    entity: "appointment",
    method: "get",

    successHandler: (res: any) => {
      setAppointmentDays(res.data.distinct_days);
    },
    failHandler: (error: any) => {
      handleError(error);
    },
    additionalRouteParts: additionalRouteParts,
    parametersToRender: {
      additionalUrlParameters: additionalUrlParameters,
      depth: "0",
    },
  });
}
